import { useState } from "react";
import { Badge, Button, Mark, Panel, PanelHeader, cn } from "@/components/ui";
import { adjustedEbitda, openExceptions } from "@/lib/finance";
import { formatConfidence, formatMoney, formatRecommendation } from "@/lib/format";
import type { DecisionOutcome, Finding } from "@/lib/types";
import { useCase } from "@/components/case/case-context";
import { TraceLink } from "@/components/case/trace";
import { AmountField } from "./amount-field";

/**
 * The decision screen.
 *
 * Everything an analyst needs to commit is here, and every claim supporting the
 * recommendation is clickable through to the page it came from. The three
 * actions sit at the bottom in the order the spec sets: the reversible ones
 * left, the committing one right.
 */
export function DecisionView() {
  const { case: kase, decision, analysis, evidence, changeAmount, setCondition, decide, pending } =
    useCase();
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState<DecisionOutcome | null>(null);

  const decided = decision.outcome !== null;
  const exceptions = openExceptions(evidence).length;
  const selectedConditions = decision.conditions.filter((condition) => condition.selected);

  const act = (outcome: DecisionOutcome) => {
    decide(outcome, note);
    setConfirming(null);
    setNote("");
  };

  return (
    <div className="mx-auto grid max-w-6xl gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Panel>
          <PanelHeader title="Credit recommendation" />
          <div className="px-5 py-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-lg font-semibold tracking-tight text-ink">
                  {formatRecommendation(decision.kind)}
                </p>
                <p className="mt-1 text-xs text-ink-muted">
                  Generated from the analysis. The figures below are yours to change.
                </p>
              </div>
              {decided ? (
                <Badge tone={decision.outcome === "rejected" ? "negative" : "positive"}>
                  {decision.outcome === "approved"
                    ? "Approved"
                    : decision.outcome === "rejected"
                      ? "Rejected"
                      : "Information requested"}
                </Badge>
              ) : null}
            </div>

            <dl className="mt-5 grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="sm:col-span-2 lg:col-span-1">
                <dt className="eyebrow">Amount</dt>
                <dd className="mt-1">
                  <AmountField
                    amount={decision.amount}
                    requested={kase.requestedAmount}
                    disabled={decided || pending}
                    onCommit={changeAmount}
                  />
                </dd>
              </div>
              <Term label="Term" value={`${decision.termMonths} months`} />
              <Term label="Pricing" value={decision.pricing} />
              <Term label="Risk grade" value={decision.riskGrade} />
              <Term label="Confidence" value={formatConfidence(decision.confidence)} />
              <Term
                label="Adjusted EBITDA"
                value={formatMoney(adjustedEbitda(analysis.bridge))}
              />
            </dl>
          </div>
        </Panel>

        <div className="grid gap-4 sm:grid-cols-2">
          <DriverList title="Positive" findings={decision.positives} kind="positive" />
          <DriverList title="Negative" findings={decision.negatives} kind="caution" />
        </div>

        <Panel>
          <PanelHeader
            title="Conditions"
            description={`${selectedConditions.length} of ${decision.conditions.length} attached to this recommendation.`}
          />
          {decision.conditions.length === 0 ? (
            <p className="px-4 py-4 text-xs text-ink-muted">
              No conditions proposed. Complete the evidence before conditions can be set.
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {decision.conditions.map((condition) => (
                <li key={condition.id}>
                  <label
                    className={cn(
                      "flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors",
                      !decided && "hover:bg-surface-sunken",
                      decided && "cursor-default",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={condition.selected}
                      disabled={decided || pending}
                      onChange={(event) => setCondition(condition.id, event.target.checked)}
                      className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
                    />
                    <span className="min-w-0">
                      <span
                        className={cn(
                          "block text-sm",
                          condition.selected ? "font-medium text-ink" : "text-ink-muted",
                        )}
                      >
                        {condition.label}
                      </span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-ink-muted">
                        {condition.rationale}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="space-y-4">
        <Panel>
          <PanelHeader title="Before you decide" />
          <ul className="divide-y divide-line text-xs">
            <Check
              ok={exceptions === 0}
              label={
                exceptions === 0
                  ? "All extracted values confirmed"
                  : `${exceptions} extracted value${exceptions === 1 ? "" : "s"} unconfirmed`
              }
            />
            <Check
              ok={kase.openQuestions.every((question) => question.resolved)}
              label={
                kase.openQuestions.filter((q) => !q.resolved).length === 0
                  ? "No open questions"
                  : `${kase.openQuestions.filter((q) => !q.resolved).length} open questions`
              }
            />
            <Check ok={selectedConditions.length > 0} label={`${selectedConditions.length} conditions attached`} />
          </ul>
        </Panel>

        <Panel>
          <PanelHeader title="Decision" />
          <div className="space-y-3 px-4 py-4">
            {decided ? (
              <p className="text-xs leading-relaxed text-ink-muted">
                This case has been decided. The full trail is on the{" "}
                <span className="font-medium text-ink">Activity</span> tab.
              </p>
            ) : (
              <>
                <label className="block">
                  <span className="eyebrow">Note (optional)</span>
                  <textarea
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    rows={3}
                    placeholder="Recorded against your name in the audit trail."
                    className="mt-1 w-full resize-y rounded-md border border-line-strong bg-surface px-2 py-1.5 text-sm"
                  />
                </label>

                {confirming ? (
                  <div className="rounded-md border border-accent-line bg-accent-soft px-3 py-3">
                    <p className="text-xs leading-relaxed text-ink">
                      {confirming === "approved"
                        ? `Record approval of ${formatMoney(decision.amount)} over ${decision.termMonths} months with ${selectedConditions.length} condition${selectedConditions.length === 1 ? "" : "s"}?`
                        : confirming === "rejected"
                          ? "Record a rejection for this case?"
                          : "Send this case back for more information?"}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <Button variant="primary" disabled={pending} onClick={() => act(confirming)}>
                        Confirm
                      </Button>
                      <Button variant="ghost" onClick={() => setConfirming(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button
                      variant="secondary"
                      className="w-full"
                      disabled={pending}
                      onClick={() => setConfirming("info_requested")}
                    >
                      Request more info
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="danger" disabled={pending} onClick={() => setConfirming("rejected")}>
                        Reject
                      </Button>
                      <Button variant="primary" disabled={pending} onClick={() => setConfirming("approved")}>
                        Approve
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Term({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="numeric mt-1 text-sm font-medium text-ink">{value}</dd>
    </div>
  );
}

function DriverList({
  title,
  findings,
  kind,
}: {
  title: string;
  findings: Finding[];
  kind: "positive" | "caution";
}) {
  return (
    <Panel>
      <PanelHeader title={title} />
      <ul className="divide-y divide-line">
        {findings.map((finding) => (
          <li key={finding.id} className="flex items-start gap-2 px-4 py-2.5">
            <Mark kind={kind} />
            <TraceLink trace={finding.trace} className="text-xs leading-relaxed text-ink">
              {finding.label}
            </TraceLink>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2 px-4 py-2.5">
      <Mark kind={ok ? "positive" : "caution"} />
      <span className={ok ? "text-ink-muted" : "text-ink"}>{label}</span>
    </li>
  );
}
